import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  YAxis,
} from "recharts";
import useThemeColors from "src/hooks/useThemeColors";
import moment from "moment";

type Datum = Record<string, number | string>;

interface InsightsChartProps {
  data?: Datum[];
  goal?: number; // optional
  xAxisKey?: string;
  showXAxis?: boolean;
  barKey?: string;
  unit?: string;
  height?: number | string;
  xLabelFormat?: string; // moment format, default "D MMM"
}

const InsightsChart: React.FC<InsightsChartProps> = ({
  data = [],
  goal,
  xAxisKey = "label",
  showXAxis = false,
  barKey = "value",
  unit = "",
  height = 140,
  xLabelFormat = "D MMM",
}) => {
  const colors = useThemeColors();
  const barColor = colors["--app-primary"] || "#7C4DFF";

  const rgba = (hex: string, a: number) => {
    const h = hex.replace("#", "");
    const n = parseInt(
      h.length === 3
        ? h
            .split("")
            .map((c) => c + c)
            .join("")
        : h,
      16
    );
    const r = (n >> 16) & 255,
      g = (n >> 8) & 255,
      b = n & 255;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  const goalStroke = useMemo(() => rgba(barColor, 0.5), [barColor]);

  // Dynamic spacing based on data length
  const chartSpacing = useMemo(() => {
    const dataLength = data.length;
    if (dataLength <= 7) {
      // Week view or less - wider bars with more space
      return { barGap: 8, barCategoryGap: "65%", barSize: 10 };
    } else if (dataLength <= 15) {
      // Two weeks view - moderate spacing
      return { barGap: 6, barCategoryGap: "40%", barSize: 8 };
    } else {
      // Month view (30+ days) - compact but readable
      return { barGap: 3, barCategoryGap: "15%", barSize: 6 };
    }
  }, [data.length]);

  const formatLabel = (value: any) => {
    const cleaned = String(value ?? "").replace("TZ", "T");
    const m = moment(cleaned, [moment.ISO_8601, "YYYY-MM-DD"], true);
    return m.isValid() ? m.format(xLabelFormat) : String(value ?? "");
  };

  const renderTooltip = (props: any) => {
    const raw = props?.payload?.[0]?.payload as Datum | undefined;
    const val = raw ? (raw[barKey] as number | string) : null;
    const label = raw?.label as string;
    return (
      <div className="bg-white border border-gray-100 rounded-md p-2 shadow-md">
        <div className="text-base font-semibold text-black">
          {val ?? "-"} {unit}
        </div>
        {label && (
          <div className="text-xs font-medium text-gray-600">
            {formatLabel(label)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full rounded-xl bg-[var(--bg-secondary,#FBFBFF)]">
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 18,
              right: 16,
              bottom: showXAxis ? 28 : 8,
              left: showXAxis ? 24 : 8,
            }}
            barGap={chartSpacing.barGap}
            barCategoryGap={chartSpacing.barCategoryGap}
          >
            <XAxis
              dataKey={xAxisKey}
              hide={!showXAxis}
              axisLine={false}
              tickLine={false}
              tickMargin={6}
              fontSize={12}
              angle={-45}
              textAnchor="end"
              tickFormatter={formatLabel}
              stroke="#A0A0A0"
            />

            <YAxis
              domain={
                typeof goal === "number" ? [0, goal * 1.3] : ["auto", "auto"]
              }
              hide
            />

            <Tooltip
              cursor={{ fill: "transparent" }}
              wrapperStyle={{ outline: "none" }}
              content={(props) => renderTooltip(props)}
            />
            <Bar
              dataKey={barKey}
              fill={barColor}
              radius={[6, 6, 0, 0]}
              barSize={chartSpacing.barSize}
            />

            {typeof goal === "number" && goal > 0 && (
              <ReferenceLine
                y={goal}
                stroke={goalStroke}
                strokeDasharray="6 6"
                strokeWidth={2}
                ifOverflow="extendDomain"
                label={({ viewBox }) => {
                  const vb = viewBox as {
                    x: number;
                    y: number;
                    width: number;
                    height: number;
                  };
                  const pillW = 110;
                  const pillH = 22;
                  const padRight = 4;
                  const x = vb.x + vb.width - pillW - padRight;
                  const y = vb.y - pillH / 2;
                  return (
                    <g>
                      <rect
                        x={x}
                        y={y}
                        width={pillW}
                        height={pillH}
                        rx={11}
                        fill={barColor}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                      <text
                        x={x + pillW / 2}
                        y={y + pillH / 2 + 4}
                        textAnchor="middle"
                        fontSize={12}
                        fill="#fff"
                        style={{ pointerEvents: "none", fontWeight: 600 }}
                      >
                        Goal: {goal} {unit}
                      </text>
                    </g>
                  );
                }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default InsightsChart;
